// The chart wheel is polar coordinates and nothing else. No astronomy happens
// on the client — Rails sends finished longitudes and this turns them into x/y.

export const SIZE = 1000;
export const CENTRE = SIZE / 2;

// Concentric rings, outermost first. Radii as fractions of half the viewBox.
export const RINGS = {
  zodiacOuter: 0.96,
  zodiacInner: 0.84,
  houseInner: 0.30,
  planets: 0.74,
  planetTicks: 0.84,
  aspects: 0.30,
  houseNumbers: 0.345
};

export const radius = (fraction) => CENTRE * fraction;

// THE ORIENTATION RULE, in one place.
//
// A chart is drawn with the Ascendant on the LEFT — due east, where things
// rise — and longitude increasing ANTICLOCKWISE. SVG's y axis points down and
// its angles run clockwise, so both have to be inverted here. Getting this
// wrong produces a chart that is a mirror of itself and still looks like a
// chart, which is why it lives in exactly one function.
export function toPoint(longitude, ascendant, r) {
  const theta = ((longitude - ascendant) * Math.PI) / 180;
  return {
    x: CENTRE - r * Math.cos(theta),
    y: CENTRE + r * Math.sin(theta)
  };
}

export const polar = (longitude, ascendant, fraction) =>
  toPoint(longitude, ascendant, radius(fraction));

// An SVG arc along a ring, from one longitude to another going forward through
// the zodiac.
export function ringArc(from, to, ascendant, fraction) {
  const r = radius(fraction);
  const start = toPoint(from, ascendant, r);
  const end = toPoint(to, ascendant, r);
  const sweep = (((to - from) % 360) + 360) % 360;
  const large = sweep > 180 ? 1 : 0;
  // sweep-flag 0: anticlockwise on screen, which is forward through the zodiac.
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y}`;
}

// One band of a ring, between two longitudes — the shape a zodiac sign
// occupies. Built explicitly rather than by stitching two arc strings
// together: the first attempt did that with a regex and closed each sector
// across the centre, filling the whole wheel grey.
//
// Out along the outer radius anticlockwise (sweep 0), in across the end cap,
// back along the inner radius clockwise (sweep 1), close.
export function annularSector(from, to, ascendant, outerFraction, innerFraction) {
  const outer = radius(outerFraction);
  const inner = radius(innerFraction);
  const sweep = (((to - from) % 360) + 360) % 360;
  const large = sweep > 180 ? 1 : 0;

  const o1 = toPoint(from, ascendant, outer);
  const o2 = toPoint(to, ascendant, outer);
  const i2 = toPoint(to, ascendant, inner);
  const i1 = toPoint(from, ascendant, inner);

  return [
    `M ${o1.x} ${o1.y}`,
    `A ${outer} ${outer} 0 ${large} 0 ${o2.x} ${o2.y}`,
    `L ${i2.x} ${i2.y}`,
    `A ${inner} ${inner} 0 ${large} 1 ${i1.x} ${i1.y}`,
    "Z"
  ].join(" ");
}

// Glyphs collide. Three planets within a couple of degrees land on top of one
// another, so they are pushed apart along the ring while a tick stays at the
// TRUE longitude — the position is preserved, the label is only made readable.
//
// One relaxation pass over neighbours, repeated until nothing moves or the
// budget runs out. Deliberately simple: it is easy to reason about, and the
// tick means an imperfect spread is still honest rather than a lie about where
// a planet is.
export const MIN_SEPARATION = 7;

const wrap = (degrees) => ((degrees % 360) + 360) % 360;

const signedGap = (from, to) => {
  const d = wrap(to - from);
  return d > 180 ? d - 360 : d;
};

export function spreadGlyphs(bodies, minSeparation = MIN_SEPARATION) {
  if (bodies.length === 0) return [];

  const placed = [...bodies]
    .sort((a, b) => a.longitude - b.longitude)
    .map((body) => ({ ...body, display: body.longitude }));

  if (placed.length === 1) return placed;

  for (let pass = 0; pass < 60; pass += 1) {
    let moved = false;
    for (let i = 0; i < placed.length; i += 1) {
      const a = placed[i];
      const b = placed[(i + 1) % placed.length];
      if (a === b) continue;

      const separation = signedGap(a.display, b.display);
      if (Math.abs(separation) >= minSeparation) continue;

      const push = (minSeparation - Math.abs(separation)) / 2;
      a.display = wrap(a.display - push);
      b.display = wrap(b.display + push);
      moved = true;
    }
    if (!moved) break;
  }

  return placed;
}
